"use client";

import React, { useEffect, useState } from "react";
import { MapPin, Clock, Globe, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-3 py-2 text-base font-medium text-foreground hover:text-primary transition-colors outline-none">
                {item.label}
                <span className="absolute left-3 right-3 bottom-0 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-out" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-4 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors block py-2 border-b border-border/50">
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function NavegacionPrincipal() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="navegación-principal" className="sticky top-0 z-50 w-full flex flex-col">
      <div className="h-10 bg-primary text-primary-foreground hidden lg:flex items-center justify-between px-4 md:px-8 text-sm font-medium">
        <div className="flex items-center gap-6">
          <Link to="https://wa.me/526149800517" newTab className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>Colegio Militar</span>
          </Link>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>8:00 AM - 5:30 PM</span>
          </div>
        </div>
        <div className="flex items-center h-full">
          <Link to="https://wa.me/526149800517" newTab className="px-4 hover:text-primary-foreground/80 transition-colors">
            Cliente Consentido
          </Link>
          <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
          <Link to="https://wa.me/526149800517" newTab className="px-4 hover:text-primary-foreground/80 transition-colors">
            Sucursales
          </Link>
          <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
          <Link to="https://wa.me/526149800517" newTab className="px-4 hover:text-primary-foreground/80 transition-colors">
            Contacto
          </Link>
          <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
          <Link to="https://wa.me/526149800517" newTab className="px-4 hover:text-primary-foreground/80 transition-colors">
            Catálogo
          </Link>
          <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
          <Link to="#" className="pl-4 flex items-center gap-2 hover:text-primary-foreground/80 transition-colors">
            <Globe className="w-4 h-4" />
            English
          </Link>
        </div>
      </div>

      <header className={cn("w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/95 backdrop-blur-lg border-border shadow-sm" : "bg-background")}>
        <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0">
            <WvcLogo className="h-10 w-auto" />
          </Link>

          <div className="hidden lg:block flex-1 px-8">
            <div className="flex justify-center">
              <MenuProvider menu_id="45">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          <div className="hidden lg:flex items-center flex-shrink-0">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 shadow-sm" onClick={() => window.open("https://wa.me/526149800517", "_blank")}>
              Cotizar
            </Button>
          </div>

          <div className="lg:hidden flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground">
                  <Menu className="w-6 h-6" />
                  <span className="sr-only">Abrir menú</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
                <div className="flex flex-col h-full">
                  <div className="mt-6">
                    <Link to="/" className="inline-block mb-6">
                      <WvcLogo className="h-10 w-auto" />
                    </Link>
                    <MenuProvider menu_id="45">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>
                  <div className="mt-auto pb-8 pt-4">
                    <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-sm" onClick={() => window.open("https://wa.me/526149800517", "_blank")}>
                      Cotizar
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </section>;
}